<template>
  <div
    class="inline-flex cursor-pointer pt-1"
    @click="handleAnimation"
  >
    <div :class="expressionButtonClasses">
      <div
        v-if="expression.content"
        :class="[$style.selected, {
          [$style.new]: animation.selected.new,
        }]"
      >
        <img
          :src="getGifUrl(expression.content)"
          :alt="expression.content + ' (animated)'"
        >
      </div>

      <div :class="$style.base">
        <img
          :src="getImgUrl('winking')"
          alt="Express your emotion"
        >
      </div>
    </div>

    <div
      ref="expressions"
      :class="$style.expressions"
      v-on-clickaway="closeExpressions"
    >
      <ul
        ref="box"
        :class="$style.box"
      >
        <li
          v-for="(expressionType, index) in expressionTypes"
          :key="expressionType"
          :class="[$style.expression, {
            [$style.selected]: expressionType === expression.content,
            [$style.show]: animation.expressions.show[expressionType],
            [$style.hide]: !animation.expressions.show[expressionType]
          }]"
          :data-index="index"
          :data-expression="expressionType"
          :data-text="$t('event.timeline.expressions.' + expressionType)"
          @click="saveInteraction(expressionType)"
        >
          <img
            :src="getImgUrl(expressionType)"
            :alt="expressionType + ' (static)'"
            :class="$style.initial"
          >

          <img
            :src="getGifUrl(expressionType)"
            :alt="expressionType + ' (animated)'"
            :class="$style.animated"
          >
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { mapState } from 'vuex'
import _ from 'lodash'

import { mixin as ClickAway } from 'vue-clickaway2'

export default {
  mixins: [ClickAway],

  props: {
    value: {
      type: Object
    },
    reactionId: {
      type: String
    }
  },

  data () {
    return {
      expression: {
        id: '',
        content: ''
      },
      expressionTypes: [
        'love',
        'lolCry',
        'wow',
        'cry',
        'angry'
      ],
      animation: {
        next: 'in',
        active: false,
        selected: {
          active: !!this.value.content,
          new: false
        },
        expressions: {
          show: {
            love: false,
            lolCry: false,
            wow: false,
            cry: false,
            angry: false
          }
        }
      }
    }
  },

  computed: {
    ...mapState({
      eventId: state => state.routeParameters.event.id,
      attendeeId: state => state.routeParameters.attendee ? state.routeParameters.attendee.id : null
    }),

    expressionButtonClasses () {
      return [this.$style['expression-button'], {
        [this.$style.content]: !!this.expression.content,
        [this.$style.enter]: this.animation.selected.active,
        [this.$style.leave]: !this.animation.selected.active
      }]
    }
  },

  created () {
    this.$nextTick(() => {
      this.expression = _.isEmpty(this.value) ? this.expression : this.value
    })
  },

  methods: {
    saveInteraction (expression) {
      this.selectExpression(expression)

      if (this.expression.id.length === 0) {
        this.createInteraction()
          .then(result => {
            this.expression.id = result.data.event.createInteraction.id
          })
      } else if (this.expression.content.length > 0) {
        this.updateInteraction()
      } else {
        this.deleteInteraction().then(() => { this.expression.id = '' })
      }
    },

    createInteraction () {
      return this.$apollo
        .mutate({
          mutation: gql`
            mutation CreateInteraction(
              $eventId: Uuid!,
              $reactionId: Uuid!,
              $attendeeId: Uuid,
              $input: CreateInteractionInput!
            ) {
              event (eventId: $eventId) {
                createInteraction(reactionId: $reactionId, attendeeId: $attendeeId, input: $input) {
                  id
                }
              }
            }
          `,
          variables: {
            eventId: this.eventId,
            reactionId: this.reactionId,
            attendeeId: this.attendeeId,
            input: {
              channel: 'timeline',
              type: 'expression',
              content: this.expression.content
            }
          }
        })
    },

    updateInteraction () {
      return this.$apollo
        .mutate({
          mutation: gql`
            mutation UpdateInteraction(
              $eventId: Uuid!,
              $interactionId: Uuid!,
              $content: String!
            ) {
              event (eventId: $eventId) {
                updateInteraction(interactionId: $interactionId, content: $content) {
                  id
                }
              }
            }
          `,
          variables: {
            eventId: this.eventId,
            interactionId: this.expression.id,
            content: this.expression.content
          }
        })
    },

    deleteInteraction () {
      return this.$apollo
        .mutate({
          mutation: gql`
            mutation DeleteInteraction(
              $eventId: Uuid!,
              $interactionId: Uuid!
            ) {
              event (eventId: $eventId) {
                deleteInteraction(interactionId: $interactionId)
              }
            }
          `,
          variables: {
            eventId: this.eventId,
            interactionId: this.expression.id
          }
        })
    },

    selectExpression (expression) {
      let isDifferentExpression = this.expression.content !== expression

      this.animation.selected.new = isDifferentExpression
      this.expression.content = isDifferentExpression ? expression : ''

      this.expressionsOutAnimation()
        .then(() => {
          this.$refs.expressions.style.zIndex = -1
          this.animation.next = 'in'
          this.animation.active = false
          this.animation.selected.active = true
        })
    },

    handleAnimation () {
      if (this.animation.active) {
        return
      }

      this.animation.selected.active = false

      if (this.animation.next === 'in') {
        this.$refs.expressions.style.zIndex = 1

        this.expressionsInAnimation()
          .then(() => {
            this.animation.next = 'out'
            this.animation.active = false
          })
      }

      if (this.animation.next === 'out') {
        this.animation.selected.new = false

        this.expressionsOutAnimation()
          .then(() => {
            this.$refs.expressions.style.zIndex = -1
            this.animation.next = 'in'
            this.animation.active = false
            this.animation.selected.active = true
          })
      }
    },

    closeExpressions () {
      if (this.animation.active) {
        return
      }

      if (this.animation.next === 'out') {
        this.animation.selected.new = false

        this.expressionsOutAnimation()
          .then(() => {
            this.$refs.expressions.style.zIndex = -1
            this.animation.next = 'in'
            this.animation.active = false
            this.animation.selected.active = true
          })
      }
    },

    expressionsInAnimation () {
      this.animation.active = true
      const boxElement = this.$refs.box

      return new Promise(resolve => {
        boxElement.style.opacity = 1

        setTimeout(() => {
          this.animateExpressions()
            .then(elements => {
              elements.forEach(element => {
                element.style.pointerEvents = 'auto'
              })

              resolve(boxElement)
            })
        }, 80)
      })
    },

    expressionsOutAnimation () {
      this.animation.active = true
      const boxElement = this.$refs.box

      return new Promise(resolve => {
        setTimeout(() => {
          this.animateExpressions()
            .then(() => {
              boxElement.style.opacity = 0

              resolve(boxElement)
            })
        }, 80)
      })
    },

    animateExpressions () {
      const expressionElements = Array.from(this.$refs.box.children)

      return new Promise(resolve => {
        let expressionIndex = 1

        expressionElements.forEach(expressionElement => {
          expressionElement.style.pointerEvents = 'none'

          setTimeout(() => {
            let expresionType = expressionElement.dataset.expression
            this.animation.expressions.show[expresionType] = !expressionElement.classList.contains(this.$style.show)

            if (expressionIndex === expressionElements.length) {
              resolve(expressionElements)
            } else {
              expressionIndex++
            }
          }, +expressionElement.dataset.index * 80)
        })
      })
    },

    getImgUrl (expression) {
      return require('@common/assets/emojis/' + expression + '/initial.png')
    },

    getGifUrl (expression) {
      return require('@common/assets/emojis/' + expression + '/animated.gif')
    }
  }
}
</script>

<style
  lang="scss"
  module
>
@import "../../../../assets/scss/math-functions-extension";

$wave-diameter: 3.5rem;
$wave-radius: .5 * $wave-diameter;
$particles-diameter: .375rem;
$particles-radius: .5 * $particles-diameter;

@mixin particles($k) {
  $shadow-list: ();
  $n-groups: 7;
  $group-base-angle: 130deg / $n-groups;
  $group-distr-r: (1 + $k * .25) * $wave-radius;
  $n-particles: 2;
  $particles-base-angle: 130 / $n-particles;
  $particles-off-angle: 30deg; // offset angle from radius
  $spread-r: -$k * $particles-radius;

  @for $i from 0 to $n-groups {
    $group-curr-angle: $i * $group-base-angle - 70deg;
    $xg: $group-distr-r * cos($group-curr-angle);
    $yg: $group-distr-r * sin($group-curr-angle);

    @for $j from 0 to $n-particles {
      $particle-curr-angle: $group-curr-angle + $particles-off-angle + $j * $particles-base-angle;
      $xs: $xg + $particles-diameter * cos($particle-curr-angle);
      $ys: $yg + $particles-diameter * sin($particle-curr-angle);

      $shadow-list: $shadow-list, $xs $ys 0 $spread-r hsl(($i + $j) * $group-base-angle, 100%, 75%);
    }
  }

  box-shadow: $shadow-list;
}

@mixin expansive_wave($ext) {
  transform: scale(1);
  border-top-color: rgba(204, 142, 245, .4);
  border-right-color: rgba(204, 142, 245, .4);
  border-bottom-color: rgba(204, 142, 245, .4);
  border-width: $ext;
}

// noinspection CssInvalidAtRule
.expressions {
  @apply absolute;

  top: -110%;
  left: 10px;
  height: 75px;
  z-index: -1;

  .box {
    @apply list-reset inline-flex items-center flex-wrap bg-white rounded-full border border-grey-90 px-2;

    width: 235px;
    height: 40px;
    opacity: 0;
    transition: opacity .3s ease;

    .expression {
      @apply relative cursor-pointer;

      width: 40px;
      transition: transform .3s cubic-bezier(.175, .885, .32, 1.275), opacity .2s ease;

      &.show {
        opacity: 1;
        transform: translateY(0);
      }

      &.hide {
        opacity: 0 !important;
        transform: translateY(50%) !important;
      }

      &:not(:last-of-type) {
        @apply mr-1;
      }

      .animated {
        @apply absolute pin-t pin-l;

        visibility: hidden;
        width: 40px;
      }

      .initial {
        filter: grayscale(95%);
        width: 40px;
      }

      &::after {
        @apply absolute rounded font-bold text-white text-center px-1 truncate;

        content: attr(data-text);
        position: absolute;
        top: -10%;
        left: 50%;
        visibility: hidden;
        border-radius: .25rem;
        line-height: 12px;
        font-size: 7px;
        background-color: rgba(0, 0, 0, .5);
        transform: translate(-50%, 10%);
        transition: transform .3s ease-in-out;
      }

      &:hover::after {
        visibility: visible;
        transform: translate(-50%, -50%);
      }

      &:hover,
      &.selected {
        transform: scale(1.6) translateY(-10%);
        opacity: 1;

        .initial {
          visibility: hidden;
        }

        .animated {
          visibility: visible;
          display: block;
        }
      }
    }
  }
}

.expression-button {
  position: relative;
  width: 35px;
  height: 35px;

  .base,
  .selected {
    transition: transform .2s, opacity .1s;
  }

  .base {
    position: absolute;
    top: 0;
    left: 0;
    filter: grayscale(95%);
    opacity: 1;
    transform: translateX(0);
    width: 35px;
  }

  .selected {
    opacity: 0;
    transform: translateX(-40%);

    &::before,
    &::after {
      position: absolute;
      z-index: -1;
      top: 50%;
      left: 50%;
      border-radius: 50%;
      content: '';
    }

    &::before {
      box-sizing: border-box;
      margin: -$wave-radius;
      border: solid $wave-radius #e2264d;
      border-left-color: transparent !important;
      border-bottom: 0;
      border-top: 0;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      width: $wave-diameter;
      height: $wave-diameter * 2;
      transform: scale(0);
    }

    &::after {
      @include particles(1);

      margin: -$particles-radius;
      width: $particles-diameter;
      height: $particles-diameter;
    }
  }

  &.enter {
    .base {
      opacity: 1;
      transform: translateX(0);
    }

    .selected {
      opacity: 0;
      transform: translateX(-40%);
    }
  }

  &.leave {
    .base {
      opacity: 1;
      transform: translateX(0);
    }

    .selected {
      opacity: 0;
      transform: translateX(-40%);
    }
  }

  &.content {
    .base {
      opacity: 0;
      transform: translateX(-40%);
    }

    .selected {
      opacity: 1;
      transform: translateX(0);
    }

    &.enter {
      .base {
        opacity: 0;
        transform: translateX(-40%);
      }

      .selected {
        opacity: 1;
        transform: translateX(0);

        &.new {
          color: #e2264d;
          animation-duration: 1s;
          animation-timing-function: cubic-bezier(.17, .89, .32, 1.49);

          &::before,
          &::after {
            animation: inherit;
            animation-timing-function: ease-out;
          }

          &::before {
            will-change: transform, border-width, border-color;
            animation-name: expansive_wave;
          }

          &::after {
            will-change: opacity, box-shadow;
            animation-name: particles;
          }
        }
      }
    }

    &.leave {
      .base {
        opacity: 1;
        transform: translateX(0);
      }

      .selected {
        opacity: 0;
        transform: translateX(-40%);
      }
    }
  }
}

@keyframes expansive_wave {
  15% {
    @include expansive_wave($wave-radius / 3);
  }

  30%,
  100% {
    @include expansive_wave(0);
  }
}

@keyframes particles {
  0%,
  20% {
    opacity: 0;
  }

  25% {
    @include particles(0);

    opacity: 1;
  }
}
</style>
